/**
 * Contenuti della pagina Novità (/news).
 *
 * Convenzione: ogni voce qui corrisponde a una entry `[public]` del CHANGELOG.md
 * (vedi sezione "Sistema Notifiche" e successive), riscritta in chiave cliente.
 * Aggiornare questo file quando si rilascia qualcosa di visibile all'utente.
 */

export type NewsCategory =
  | "Novità"
  | "Miglioramento"
  | "Correzione"
  | "Sicurezza";

export interface NewsItem {
  category: NewsCategory;
  title: string;
  description: string;
}

export interface NewsRelease {
  /** Etichetta data mostrata (es. "6 giugno 2026"). */
  date: string;
  /** Titolo opzionale del gruppo di novità. */
  headline?: string;
  items: NewsItem[];
}

/**
 * Annuncio mostrato UNA volta al primo accesso dopo un rilascio.
 *
 * `id` è la chiave con cui si ricorda che l'utente l'ha già visto: cambiarlo fa ricomparire il
 * popup a tutti, quindi si cambia solo quando c'è davvero qualcosa di nuovo da annunciare.
 * Tenere pochi punti: chi vuole il dettaglio ha la pagina Novità, raggiungibile dal popup stesso.
 */
export interface WhatsNew {
  id: string;
  title: string;
  intro: string;
  bullets: { title: string; text: string }[];
}

export const whatsNew: WhatsNew = {
  id: "2026-07-rimozioni",
  title: "Come lavoriamo alle tue rimozioni",
  intro:
    "Abbiamo cambiato il modo in cui seguiamo le richieste di rimozione. Ecco cosa vedrai di diverso.",
  bullets: [
    {
      title: "Ogni rimozione la verifichiamo noi",
      text: "Non ci fidiamo della parola del sito: un contenuto risulta rimosso solo dopo due controlli indipendenti a distanza di ore. E continuiamo a sorvegliare anche quelli già rimossi, così se ricompaiono ce ne accorgiamo.",
    },
    {
      title: "Distinguiamo i contenuti dalle pagine di ricerca",
      text: "Alcuni indirizzi non sono contenuti ma pagine che il sito genera quando qualcuno cerca un nome: nessun gestore può cancellarle. Ora le trattiamo a parte, chiedendone la rimozione ai motori di ricerca, e non le contiamo tra i contenuti da rimuovere.",
    },
    {
      title: "Se un sito non risponde, non ce ne dimentichiamo",
      text: "Dopo 14 giorni di silenzio il caso torna automaticamente sul nostro tavolo e ripartiamo con un sollecito; a 30 giorni di nuovo. Se dopo 60 giorni il contenuto è ancora online, valutiamo con te l'azione legale, con la storia completa dei tentativi. Non serve che ce lo ricordi tu.",
    },
    {
      title: "Percentuali più accurate nel report",
      text: "La percentuale di rimozione è ora calcolata solo sui contenuti che si possono effettivamente togliere. Prima il conteggio comprendeva anche le pagine che nessun gestore può rimuovere, e questo la faceva risultare più bassa del lavoro realmente svolto.",
    },
  ],
};

export const newsReleases: NewsRelease[] = [
  {
    date: "28 luglio 2026",
    headline: "Come lavoriamo alle tue rimozioni",
    items: [
      {
        category: "Miglioramento",
        title: "Ogni rimozione è verificata due volte",
        description:
          "Un contenuto risulta rimosso solo dopo due controlli indipendenti a distanza di ore: un sito momentaneamente irraggiungibile non viene più scambiato per una rimozione riuscita.",
      },
      {
        category: "Novità",
        title: "Sorveglianza sui contenuti già rimossi",
        description:
          "Continuiamo a ricontrollare anche ciò che è già stato tolto. Se qualcuno prova a ripubblicarlo, il contenuto torna in lavorazione da solo e la richiesta riparte.",
      },
      {
        category: "Novità",
        title: "Pagine di ricerca trattate a parte",
        description:
          "Alcuni indirizzi non sono contenuti: sono pagine che il sito genera al momento in cui qualcuno cerca un nome, e nessun gestore può cancellarle. Ora vengono indirizzate ai motori di ricerca invece che al sito, e non pesano più sul conteggio delle rimozioni.",
      },
      {
        category: "Novità",
        title: "Nessun caso resta indietro in silenzio",
        description:
          "Se un sito non risponde, dopo 14 giorni il caso torna automaticamente in lavorazione e ripartiamo con un sollecito; a 30 giorni di nuovo. Se dopo 60 giorni il contenuto è ancora online, il caso viene segnalato per la valutazione legale con la storia completa dei tentativi.",
      },
      {
        category: "Miglioramento",
        title: "Rimozione dai motori di ricerca tracciata",
        description:
          "Le richieste inviate a Google e Bing sono ora registrate con data ed esito, così nel report vedi separatamente cosa è stato tolto dai siti e cosa è sparito dai risultati di ricerca.",
      },
      {
        category: "Miglioramento",
        title: "Percentuali di rimozione più accurate",
        description:
          "La percentuale è ora calcolata solo sui contenuti effettivamente rimovibili. Prima il denominatore comprendeva anche le pagine che nessun gestore può cancellare, e questo la faceva risultare più bassa del lavoro realmente svolto.",
      },
    ],
  },
  {
    date: "9 giugno 2026",
    headline: "Ricerche più precise e in italiano",
    items: [
      {
        category: "Miglioramento",
        title: "Ricerche più complete",
        description:
          "Ogni ricerca ora considera automaticamente le varianti del nome (plurali e femminili), così non ti sfuggono risultati rilevanti.",
      },
      {
        category: "Miglioramento",
        title: "Sentiment più chiaro, in italiano",
        description:
          "La valutazione di ogni risultato ora è descritta in italiano e basata sull'impatto reale sulla tua reputazione: un contenuto dannoso resta «Negativo» anche se scritto con toni gentili.",
      },
    ],
  },
  {
    date: "6 giugno 2026",
    headline: "Mia Reputation ora ti avvisa",
    items: [
      {
        category: "Novità",
        title: "Notifiche in tempo reale",
        description:
          "Un nuovo campanellino 🔔 in alto ti segnala subito quando un report è pronto, una ricerca è completata o cambia lo stato di una richiesta di rimozione.",
      },
      {
        category: "Novità",
        title: "Avvisi via email",
        description:
          "Ricevi un'email quando la tua ricerca è completata o il tuo report è pronto, così resti aggiornato anche quando non sei nell'app.",
      },
      {
        category: "Novità",
        title: "Riepilogo rimozioni",
        description:
          "Gli aggiornamenti sulle richieste di rimozione arrivano raccolti in un'unica email riepilogativa periodica, senza intasare la casella con un messaggio per ogni cambiamento.",
      },
      {
        category: "Miglioramento",
        title: "Analisi più veloce",
        description:
          "L'analisi del sentiment dei risultati di ricerca è ora molto più rapida: i dati compaiono progressivamente mentre la ricerca è ancora in lavorazione.",
      },
    ],
  },
  {
    date: "1 giugno 2026",
    items: [
      {
        category: "Novità",
        title: "Contatore rimozioni",
        description:
          "In dashboard vedi a colpo d'occhio quante rimozioni hai utilizzato rispetto al tuo piano.",
      },
      {
        category: "Miglioramento",
        title: "Nuova veste grafica",
        description:
          "Pagine di accesso e landing ridisegnate con il brand Mia Reputation, più chiare e coerenti.",
      },
      {
        category: "Correzione",
        title: "Layout più stabile",
        description:
          "Risolti alcuni problemi di visualizzazione (footer sempre visibile e scroll) su diverse pagine.",
      },
    ],
  },
  {
    date: "23 maggio 2026",
    items: [
      {
        category: "Novità",
        title: "Nuovo indirizzo dell'app",
        description:
          "L'app è ora raggiungibile su app.miareputation.com, più coerente con il marchio.",
      },
      {
        category: "Miglioramento",
        title: "Da WebReputation a Mia Reputation",
        description:
          "Completato il rebranding in tutta l'applicazione: nuovo nome, nuovo look.",
      },
    ],
  },
];

/** Anteprime di ciò che sta per arrivare (entry `[public]` ancora in [Unreleased]). */
export const upcomingNews: NewsItem[] = [
  {
    category: "Novità",
    title: "EMERGENCY Crisis Pack",
    description:
      "Un pacchetto one-shot dedicato alle crisi reputazionali acute, per intervenire in modo immediato.",
  },
  {
    category: "Novità",
    title: "Coupon WELCOME30",
    description:
      "Uno sconto del 30% sul primo addebito per i nuovi clienti.",
  },
];
